import { useGiftWrapContext } from '../../../context/GiftWrapContext.jsx';
import { usePage } from '../../../context/PageContext.jsx';
import { useUserInput } from '../../../context/UserInputContext.tsx';
import { giftWrapEventListener } from '../../../services/giftWrapEventListener.js';
import { logError } from '../../../utilities/logging.js';

/**
 * Custom hook for GiftWrapFooter logic and className selection.
 * Uses the new functional gift wrap service for cart operations.
 * @returns {Object} View model for the footer component.
 */
export function useViewModel() {
  const {
    priceModalOpen,
    setPriceModalOpen,
    personalisationChoices,
    setGiftWrapModalOpen,
    activeProduct,
    calculateGiftWrapTotal,
    calculateTotalPrice,
    resetGiftWrapState,
    addToCartLoading,
    setAddToCartLoading,
    skipUvInsertsFlow
  } = useGiftWrapContext();

  const { goToPage, getPageSelection, currentPage, getPageHasSelections } = usePage();
  const userInput = useUserInput();
  const { getImageInput, getTextInput, visualiserSelection } = userInput;

  /**
   * Handles arrow click to open price modal.
   * @returns {void}
   */
  const handleArrowClick = () => {
    setPriceModalOpen(true);
  };

  /**
   * Handles adding gift wrap to the last added product.
   * Uses the new functional service that preserves existing cart logic.
   * @returns {Promise<void>}
   */
  const handleAddGiftWrap = async () => {
    try {
      // Set loading state to true
      setAddToCartLoading(true);

      // Validate that we have the required data.
      if (!activeProduct?.id || !activeProduct?.title) {
        throw new Error('Active product information is missing');
      }

      if (!Array.isArray(personalisationChoices)) {
        throw new Error('Personalisation choices must be an array');
      }

      // Add gift wrap to the pending product using the event listener with user input.
      await giftWrapEventListener.addGiftWrapToPendingProduct(personalisationChoices, userInput, currentPage);

      // Handle successful addition.
      setGiftWrapModalOpen(false);
      // Reset all gift wrap state to default values.
      resetGiftWrapState();
    } catch (error) {
      logError({
        label: 'Footer useViewModel.handleAddGiftWrap',
        error,
        message: 'Failed to add gift wrap to cart',
        extraData: {
          activeProduct,
          personalisationChoicesCount: personalisationChoices?.length || 0
        }
      });

      // Re-throw the error so the UI can handle it appropriately.
      throw error;
    } finally {
      // Always set loading state to false
      setAddToCartLoading(false);
    }
  };

  /**
   * Handles skipping gift wrap and adding only the base product.
   * Since we're using the new flow, this just closes the modal.
   * The base product should already be in the cart from the original add to cart.
   * @returns {void}
   */
  const handleSkipGiftWrap = () => {
    try {
      // Close the gift wrap modal.
      setGiftWrapModalOpen(false);

      // Reset all gift wrap state to default values.
      resetGiftWrapState();

      // Clear user input (photos, text, etc.).
      userInput.clearAll();

      // Clear the pending data from the event listener since we're not adding gift wrap.
      giftWrapEventListener.clearPendingData();
    } catch (error) {
      logError({
        label: 'Footer useViewModel.handleSkipGiftWrap',
        error,
        message: 'Failed to skip gift wrap',
        extraData: { activeProduct }
      });
    }
  };

  // getPageHasSelections is now provided by PageContext and is config/userInput aware.

  /**
   * Handles button click based on the button configuration.
   * Routes to the appropriate action (add to cart, skip, or navigate to another page).
   * Supports three types of clicksTo configurations:
   * 1. String: 'pageName'
   * 2. Conditional object: { noSelections: 'page1', hasSelections: 'page2' }
   * 3. Selections array: { selections: ['page1', 'page2', 'FINISHED'] }
   *    - Uses page-specific selection from PageContext to determine which item in array to navigate to.
   *    - Index 0 goes to selections[0], index 1 goes to selections[1], otherwise goes to last item.
   * @param {Object} button - The button configuration object.
   * @param {string} currentPageName - The current page name for context-aware selection checking.
   * @returns {void}
   */
  const handleButtonClick = (button, currentPageName) => {
    // Use page-aware hasSelections check.
    const hasSelections = getPageHasSelections(currentPageName);
    let destination = button.clicksTo;

    // Handle object-based clicksTo.
    if (typeof destination === 'object' && !Array.isArray(destination)) {
      // Check if it's a selections array configuration.
      if (destination.selections && Array.isArray(destination.selections)) {
        // Navigate based on the selected option index for the current page.
        // If index is 0, go to selections[0], if 1, go to selections[1], otherwise go to last item.
        const selectionsArray = destination.selections;
        const selectedOptionIndex = getPageSelection(currentPageName);

        if (
          selectedOptionIndex !== null &&
          typeof selectedOptionIndex === 'number' &&
          selectedOptionIndex >= 0 &&
          selectedOptionIndex < selectionsArray.length - 1
        ) {
          // eslint-disable-next-line security/detect-object-injection
          destination = selectionsArray[selectedOptionIndex];
        } else {
          // Default to last item in array (usually 'FINISHED').
          destination = selectionsArray[selectionsArray.length - 1];
        }
      } else {
        // Handle conditional navigation (hasSelections/noSelections).
        destination = hasSelections ? destination.hasSelections : destination.noSelections;
      }
    }

    // Handle "Continue without gift wrap" button click.
    // Normally this proceeds to the personal-touch (UV inserts) step without a wrap.
    // But when there are no UV inserts to show (skipUvInsertsFlow), there is no step to
    // enter — so this must simply close the modal WITHOUT adding the gift wrap product,
    // rather than collapsing to FINISHED (which would add it).
    if (destination === 'skipToAddPersonalTouch') {
      if (skipUvInsertsFlow) {
        handleSkipGiftWrap();
        return;
      }
      destination = 'addPersonalTouch';
    }

    // "Continue" (with a wrap selected) when there are no UV inserts: collapse the
    // personal-touch step straight to FINISHED, which adds the selected gift wrap.
    if (skipUvInsertsFlow && destination === 'addPersonalTouch') {
      destination = 'FINISHED';
    }

    // Handle page navigation.
    if (typeof destination === 'string') {
      // Navigate to the page (PageContext will handle selection clearing and FINISHED state).
      goToPage(destination);
    }
  };

  // Use pre-calculated values from context (now useMemo values, not functions).
  const personalisationPrice = calculateGiftWrapTotal;
  const totalPrice = calculateTotalPrice;

  /**
   * Returns the inserts/cards visualiser price in currency units, if a selected variant exists.
   * The variant price from Shopify JSON is in cents and needs converting.
   * @param {Object} options - The options for getting visualiser price.
   * @param {any} options.visualiserSelection - The UserInputContext visualiser selection.
   * @returns {number} The additional price (e.g. 4.99), or 0 when not selected.
   */
  const getVisualiserPrice = ({ visualiserSelection }) => {
    if (!visualiserSelection || typeof visualiserSelection !== 'object') return 0;
    const variant = visualiserSelection.variant || null;
    const cents = typeof variant?.price === 'number' ? Number(variant.price) : null;
    if (cents == null || Number.isNaN(cents)) return 0;
    return cents / 100;
  };

  // Displayed total should match the summary rows (base item + gift wrap + insert/card).
  const displayedTotalPrice = totalPrice + getVisualiserPrice({ visualiserSelection });

  return {
    priceModalOpen,
    personalisationChoices,
    personalisationPrice,
    activeProduct,
    handleArrowClick,
    handleAddGiftWrap,
    handleSkipGiftWrap,
    handleButtonClick,
    calculateTotalPrice,
    totalPrice,
    displayedTotalPrice,
    addToCartLoading,
    skipUvInsertsFlow
  };
}
